import {
  ApolloClient,
  createHttpLink,
  from,
  NormalizedCacheObject,
  ServerParseError,
} from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';

import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { INVITE_CODE_QUERY_PARAM } from '@modules/users/constants';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import { captureMessage } from '@sentry/react';
import ApolloCache from 'ApolloCache';
import { debounce } from 'lodash';
import AuthService from 'services/AuthService';
import { v4 } from 'uuid';
import { createAnalyticsTracker } from 'v2/analytics/events';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { getApolloErrorCodes } from 'v2/core/repository/edge-server/error';
import { errorLoggerMiddleware } from 'v2/utils/errorLoggerMiddleware';

const trackForceLogoutEvents = createAnalyticsTracker('forceLogout');

// use debounce so forced logouts error on parallel calls doesn't lead to
// multiple events
const emitForceLogoutEvent = debounce(
  (operationName: string, errorCode?: ErrorCode) =>
    trackForceLogoutEvents({
      eventName: 'Forced User Logout',
      fromURL: window.location.href,
      operationName,
      errorCode,
    }),
  2000,
);

const forceLogout = (
  operationName: string,
  errorCode?: ErrorCode,
  error?: ErrorResponse,
) => {
  console.log(
    'Slug trace',
    operationName,
    errorCode,
    JSON.stringify(error?.graphQLErrors ?? {}),
  );

  emitForceLogoutEvent(operationName, errorCode);
  captureMessage(
    `Force logged out from operation: ${operationName} with error code: ${
      errorCode ?? 'N/A'
    }`,
    'info',
  );
  window.location.href = '/logout';
};

const logoutLink = onError((error: ErrorResponse) => {
  if (
    // to prevent multiple redirections after logout
    window.location.pathname === '/logout' ||
    window.location.pathname === '/login'
  ) {
    return;
  }

  const is401NetworkError =
    (error.networkError as ServerParseError)?.statusCode === 401;

  const isUnauthorizedRequest = isErrorCode(
    error,
    ErrorCode.UnauthorizedRequest,
  );

  const operationName = error.operation.operationName;

  const errorCode = getApolloErrorCodes(error)?.[0];

  if (is401NetworkError || isUnauthorizedRequest) {
    forceLogout(operationName, errorCode, error);
    return;
  }

  if (error.operation.operationName === 'getUserByEmail') {
    return; // we are already handling errors from this query in index.js
  }

  if (isErrorCode(error, ErrorCode.TrialExpired)) {
    forceLogout(operationName, errorCode, error);
    return;
  }

  if (isErrorCode(error, ErrorCode.InvalidSsoIdpToken)) {
    forceLogout(operationName, errorCode, error);
    return;
  }

  const isUserSuspended = isErrorCode(error, ErrorCode.UserSuspended);
  const isUserUnapproved = isErrorCode(error, ErrorCode.UserUnapproved);

  const search = new URLSearchParams(window.location.search);
  const hasInviteCodeParam = Boolean(search.get(INVITE_CODE_QUERY_PARAM));

  if (isUserSuspended || (isUserUnapproved && !hasInviteCodeParam)) {
    forceLogout(operationName, errorCode, error);
    trackUserManagementEvents({
      eventName: isUserSuspended
        ? 'User Management - Force Logged Out Suspended User'
        : 'User Management - Force Logged Out Unapproved User',
      userEmail: AuthService.getEmail(),
    });
  }
});

const refreshTokenIfRequired = setContext(async () => {
  await AuthService.refreshTokenIfRequired();
});

const authLink = setContext((_, { headers }) => {
  const tenantSlug = AuthService.getTenantSlug();

  const requestHeaders = {
    ...headers,
    'X-Entr-Trace-Id': v4(),
    Authorization: AuthService.isAuthenticated()
      ? `Bearer ${AuthService.getToken()}`
      : '',
    slug: headers?.slug || tenantSlug,
  };

  if (!requestHeaders.slug) {
    console.log(
      'Slug trace',
      'No slug found in headers',
      JSON.stringify(headers ?? {}),
    );
  }

  return { headers: requestHeaders };
});

// try again if the request fails with unauthorized error before logging out. This is intended to fix the race condition which caused the error "token expired by 1second".
const retryLink = new RetryLink({
  delay: {
    initial: 100,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 2,
    retryIf: (error, _operation) => {
      const isUnauthorizedRequest = isErrorCode(
        error,
        ErrorCode.UnauthorizedRequest,
      );
      return isUnauthorizedRequest;
    },
  },
});

let apolloClient: ApolloClient<NormalizedCacheObject>;
export function getApolloClient(reset = false) {
  if (reset || apolloClient == null) {
    let fetchAPI = window.fetch;

    try {
      fetchAPI = window.apiWindow?.fetch || window.fetch;
    } catch (err) {
      captureMessage('Failed to override fetchAPI', {
        extra: {
          error: err,
        },
      });
    }

    const httpLink = createHttpLink({
      uri: process.env.REACT_APP_GRAPHQL_URL,
      fetch: fetchAPI,
    });

    apolloClient = new ApolloClient({
      cache: ApolloCache,
      link: from([
        refreshTokenIfRequired,
        authLink,
        retryLink,
        logoutLink,
        errorLoggerMiddleware,
        httpLink,
      ]),
    });
  }

  return apolloClient;
}
